import { Box } from '@mui/material';
import React, { RefObject, useEffect, useRef } from 'react';

import { createPortalRenderer, PortalRenderer } from './portalRenderer';
import { SleepSound } from './soundEngine';
import { BANDS, bandsFromBins, simulateBands } from './spectrum';

interface Props {
    analyser: () => AnalyserNode | null;
    sound: SleepSound | null;
    /** The element the portal centers on and sizes itself to. */
    stage: RefObject<HTMLElement>;
}

/** New wavetable rows per second: how fast the sound ripples outward. */
const ROW_MS = 70;

const reducedMotion = (): boolean =>
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** The full-screen portal behind the sleep sounds. With reduced motion it is one still frame, redrawn on resize. */
export const Visualizer: React.FC<Props> = ({ analyser, sound, stage }) => {
    const canvas = useRef<HTMLCanvasElement>(null);
    // Read each frame: rebuilding the renderer on play and stop would blank the portal and jump its colours.
    const current = useRef(sound);
    const redrawStill = useRef<(() => void) | null>(null);

    useEffect(() => {
        const el = canvas.current;
        if (!el) return;
        const still = reducedMotion();
        const bands = new Float32Array(BANDS);
        const started = performance.now();
        let bins: Uint8Array | null = null;
        let renderer: PortalRenderer | null = null;
        let frame = 0;
        let lastRow = -Infinity;
        let energy = 0;

        const readBands = (seconds: number) => {
            const sound = current.current;
            const node = sound ? analyser() : null;
            if (node) {
                if (bins?.length !== node.frequencyBinCount) {
                    bins = new Uint8Array(node.frequencyBinCount);
                }
                node.getByteFrequencyData(bins);
                // All zeros: the context is suspended or still starting.
                if (bandsFromBins(bins, bands) > 0) return;
            }
            simulateBands(sound, seconds, bands);
        };

        const paint = (seconds: number) => {
            if (!renderer) return;
            const box = stage.current?.getBoundingClientRect();
            const page = el.getBoundingClientRect();
            renderer.draw({
                centerX: box
                    ? box.left - page.left + box.width / 2
                    : page.width / 2,
                centerY: box
                    ? box.top - page.top + box.height / 2
                    : page.height / 2,
                radius:
                    Math.min(
                        box?.width ?? page.width,
                        box?.height ?? page.height
                    ) / 2,
                seconds,
                energy,
            });
        };

        const tick = (now: number) => {
            const seconds = (now - started) / 1000;
            if (now - lastRow >= ROW_MS) {
                lastRow = now;
                readBands(seconds);
                renderer?.push(bands);
                const mean = bands.reduce((a, v) => a + v, 0) / bands.length;
                energy += (mean - energy) * 0.12;
            }
            paint(seconds);
            frame = requestAnimationFrame(tick);
        };

        const drawStill = () => {
            simulateBands(current.current, 0, bands);
            // Fill the whole wavetable, since no later frame will.
            for (let row = 0; row < 32; row++) renderer?.push(bands);
            energy = bands.reduce((a, v) => a + v, 0) / bands.length;
            paint(0);
        };

        const begin = () => {
            renderer = createPortalRenderer(el, BANDS);
            if (!renderer) return;
            if (still) drawStill();
            else frame = requestAnimationFrame(tick);
        };
        const onLost = (event: Event) => {
            // Without preventDefault the browser would never give the context back.
            event.preventDefault();
            cancelAnimationFrame(frame);
            renderer = null;
        };
        const onResize = () => still && paint(0);

        redrawStill.current = still ? drawStill : null;
        begin();
        el.addEventListener('webglcontextlost', onLost);
        el.addEventListener('webglcontextrestored', begin);
        window.addEventListener('resize', onResize);
        return () => {
            cancelAnimationFrame(frame);
            el.removeEventListener('webglcontextlost', onLost);
            el.removeEventListener('webglcontextrestored', begin);
            window.removeEventListener('resize', onResize);
            redrawStill.current = null;
            renderer?.dispose();
        };
    }, [analyser, stage]);

    useEffect(() => {
        current.current = sound;
        redrawStill.current?.();
    }, [sound]);

    return (
        <Box
            component="canvas"
            ref={canvas}
            aria-hidden
            sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                display: 'block',
            }}
        />
    );
};
